/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosError } from 'axios';
import { Api } from '../axiosApi';

export type TPeriodoNotas = 'mensal' | 'anual' | 'atual';
export type TAgrupamentoNotas = 'dia' | 'mes';

export interface IGraficoNotasItem {
  data: string;
  valornfe: number;
  qtdenfe: number;
  valornferemessa: number;
  qtdenferemessa: number;
  valornfedevolucao: number;
  qtdenfedevolucao: number;
  valornfecancelada: number;
  qtdenfecancelada: number;
  valornfce: number;
  qtdenfce: number;
  valortotal: number;
  qtdetotal: number;
  valorcompra: number;
  qtdecompra: number;
  saldo: number;
}

export interface ITopProdutoItem {
  idproduto: string;
  descricao: string | null;
  ean: string | null;
  quantidade: number;
  valor: number;
}

export interface IDashboardNotasGraficos {
  intervalo: {
    datainicial: string;
    datafinal: string;
  };
  agrupamento: TAgrupamentoNotas;
  totais: {
    valornfe: number;
    qtdenfe: number;
    valornferemessa: number;
    qtdenferemessa: number;
    valornfedevolucao: number;
    qtdenfedevolucao: number;
    valornfecancelada: number;
    qtdenfecancelada: number;
    valornfce: number;
    qtdenfce: number;
    valortotal: number;
    qtdetotal: number;
    valorcompra: number;
    qtdecompra: number;
    saldo: number;
  };
  grafico: IGraficoNotasItem[];
  topProdutosQuantidade: ITopProdutoItem[];
  topProdutosValor: ITopProdutoItem[];
}

interface ErrorResponse {
  errors: {
    default: string;
  };
}

const getDashboardNotasGraficos = async (
  periodo: TPeriodoNotas = 'atual',
  ano?: number,
  mes?: number,
): Promise<IDashboardNotasGraficos | Error> => {
  try {
    let urlRelativa = `/dashboardnotasgraficos?periodo=${periodo}`;

    if (periodo !== 'atual' && ano) urlRelativa += `&ano=${ano}`;
    if (periodo === 'mensal' && mes) urlRelativa += `&mes=${mes}`;

    const { data } = await Api.get(urlRelativa);

    if (data) {
      const converterItem = (item: any): IGraficoNotasItem => ({
        data: item.data,
        valornfe: Number(item.valornfe || 0),
        qtdenfe: Number(item.qtdenfe || 0),
        valornferemessa: Number(item.valornferemessa || 0),
        qtdenferemessa: Number(item.qtdenferemessa || 0),
        valornfedevolucao: Number(item.valornfedevolucao || 0),
        qtdenfedevolucao: Number(item.qtdenfedevolucao || 0),
        valornfecancelada: Number(item.valornfecancelada || 0),
        qtdenfecancelada: Number(item.qtdenfecancelada || 0),
        valornfce: Number(item.valornfce || 0),
        qtdenfce: Number(item.qtdenfce || 0),
        valortotal: Number(item.valortotal || 0),
        qtdetotal: Number(item.qtdetotal || 0),
        valorcompra: Number(item.valorcompra || 0),
        qtdecompra: Number(item.qtdecompra || 0),
        saldo: Number(item.saldo || 0),
      });

      const converterTopProduto = (item: any): ITopProdutoItem => ({
        idproduto: item.idproduto,
        descricao: item.descricao,
        ean: item.ean,
        quantidade: Number(item.quantidade || 0),
        valor: Number(item.valor || 0),
      });

      return {
        intervalo: data.intervalo,
        agrupamento: data.agrupamento,
        totais: {
          valornfe: Number(data.totais?.valornfe || 0),
          qtdenfe: Number(data.totais?.qtdenfe || 0),
          valornferemessa: Number(data.totais?.valornferemessa || 0),
          qtdenferemessa: Number(data.totais?.qtdenferemessa || 0),
          valornfedevolucao: Number(data.totais?.valornfedevolucao || 0),
          qtdenfedevolucao: Number(data.totais?.qtdenfedevolucao || 0),
          valornfecancelada: Number(data.totais?.valornfecancelada || 0),
          qtdenfecancelada: Number(data.totais?.qtdenfecancelada || 0),
          valornfce: Number(data.totais?.valornfce || 0),
          qtdenfce: Number(data.totais?.qtdenfce || 0),
          valortotal: Number(data.totais?.valortotal || 0),
          qtdetotal: Number(data.totais?.qtdetotal || 0),
          valorcompra: Number(data.totais?.valorcompra || 0),
          qtdecompra: Number(data.totais?.qtdecompra || 0),
          saldo: Number(data.totais?.saldo || 0),
        },
        grafico: (data.grafico || []).map(converterItem),
        topProdutosQuantidade: (data.topProdutosQuantidade || []).map(converterTopProduto),
        topProdutosValor: (data.topProdutosValor || []).map(converterTopProduto),
      };
    }

    return new Error('Erro ao listar os registros.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;

      if (axiosError.response) {
        const errorMessage =
          axiosError.response.data.errors?.default ||
          'Erro desconhecido';

        return new Error(errorMessage);
      }

      if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      }

      return new Error(axiosError.message);
    }

    return new Error('Erro inesperado');
  }
};

export const GraficosNotasService = {
  getDashboardNotasGraficos,
};
